"use strict";
/**
 * PARTNER BALANCE — the payout number, derived from rows and nothing else.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 * ---------------------------------------------------------------------------
 *
 * affiliate-service keeps three DENORMALISED counters on the partner row —
 * `totalEarnings`, `pendingEarnings`, `paidEarnings` — incremented at
 * conversion and decremented on forfeit/payout. The portal dashboard reads
 * those counters, while payout eligibility is computed by querying the
 * referral rows with `status = 'available'`. So two different numbers gate the
 * same action, and a single missed decrement (a forfeit that threw after the
 * status write, a retried webhook, a payout marked paid twice) leaves them
 * permanently disagreeing with no way to tell which is right.
 *
 * A counter cannot be audited. The rows can. Every figure here is therefore
 * recomputed from the referral and payout rows on each call — integer cents
 * throughout, no floats, no counters read.
 *
 * ---------------------------------------------------------------------------
 * THE RULE THAT MAKES IT PRECISE: TIME, NOT JUST STATUS
 * ---------------------------------------------------------------------------
 *
 * `processClearedCommissions` is a DAILY cron (06:00 UTC) that flips a
 * referral from 'clearing' to 'available' once `clearingDate` has passed. That
 * means for up to 24 hours — and indefinitely if the cron fails, throttles, or
 * is mid-deploy — money that HAS cleared still carries `status: 'clearing'`.
 *
 * Reading status alone would under-report a partner's cleared balance and
 * refuse a payout they are genuinely owed. So a row counts as cleared when
 * EITHER its status says so OR its `clearingDate` has passed. The cron becomes
 * a notification trigger rather than the source of truth, and a late cron can
 * no longer cost a partner money.
 *
 * The converse — a row marked 'available' whose clearingDate is somehow still
 * in the future — is honoured as cleared too: the status is an explicit
 * decision (an admin may clear early) and must win over the schedule.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_CLEARING_DAYS = exports.CLEARING_DAY_OPTIONS = void 0;
exports.isClearingDays = isClearingDays;
exports.resolveClearingDays = resolveClearingDays;
exports.clearingDateFor = clearingDateFor;
exports.rowHasCleared = rowHasCleared;
exports.computePartnerBalance = computePartnerBalance;
/** The clearing windows an operator may choose. Anything else is rejected. */
exports.CLEARING_DAY_OPTIONS = [15, 30, 90];
/** Used when neither the partner nor global settings specify one. */
exports.DEFAULT_CLEARING_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;
function isClearingDays(v) {
    return typeof v === 'number' && exports.CLEARING_DAY_OPTIONS.includes(v);
}
/**
 * PARTNER OVERRIDE -> GLOBAL SETTING -> 30.
 *
 * Deliberately the same precedence shape as `resolveCommissionTerms`. An
 * unrecognised or malformed value at either level is IGNORED rather than
 * trusted: a typo'd `clearingDays: 3000` must not strand a partner's money for
 * eight years, and `0` must not clear it instantly.
 */
function resolveClearingDays(partner, settings) {
    if (isClearingDays(partner?.clearingDays))
        return partner.clearingDays;
    if (isClearingDays(settings?.clearingDays))
        return settings.clearingDays;
    return exports.DEFAULT_CLEARING_DAYS;
}
/** When a commission earned at `earnedAt` becomes withdrawable. */
function clearingDateFor(earnedAt, days) {
    return earnedAt + days * DAY_MS;
}
/** Integer cents, never negative, never NaN. Cents are whole by definition. */
const cents = (v) => {
    const n = typeof v === 'number' && Number.isFinite(v) ? Math.trunc(v) : 0;
    return n > 0 ? n : 0;
};
const FORFEITED = new Set(['forfeited', 'churned', 'reversed', 'refunded']);
const CLEARED_STATUS = new Set(['available', 'cleared']);
/** A payout in any of these has already claimed its money. */
const COMMITTED_PAYOUT = new Set(['pending', 'processing', 'approved', 'paid', 'completed']);
/**
 * Has this row's clearing window closed?
 *
 * An explicit cleared status wins outright (an admin may release early). A row
 * with neither a cleared status nor a `clearingDate` has NO schedule at all and
 * is never auto-cleared — it is counted as still clearing and surfaced through
 * `unscheduledCount` rather than silently becoming payable. Money must not
 * become withdrawable because a field was missing.
 */
function rowHasCleared(row, now) {
    if (row.status && CLEARED_STATUS.has(row.status))
        return true;
    if (typeof row.clearingDate !== 'number' || !Number.isFinite(row.clearingDate))
        return false;
    return row.clearingDate <= now;
}
/**
 * THE payout figure. Derived from rows only — pass every referral/event row for
 * the partner and every payout row, and this is the whole picture.
 *
 * `now` is injected rather than read from the clock so the result is
 * deterministic and testable, and so a report can be run "as at" a past date.
 */
function computePartnerBalance(rows, payouts, now) {
    let clearingCents = 0;
    let clearedCents = 0;
    let forfeitedCents = 0;
    let unscheduledCount = 0;
    for (const row of rows ?? []) {
        const amount = cents(row?.commission);
        if (amount === 0)
            continue;
        if (row.status && FORFEITED.has(row.status)) {
            forfeitedCents += amount;
            continue;
        }
        if (rowHasCleared(row, now)) {
            clearedCents += amount;
        }
        else {
            clearingCents += amount;
            const scheduled = typeof row.clearingDate === 'number' && Number.isFinite(row.clearingDate);
            if (!scheduled)
                unscheduledCount += 1;
        }
    }
    let committedCents = 0;
    for (const p of payouts ?? []) {
        if (p?.status && COMMITTED_PAYOUT.has(p.status))
            committedCents += cents(p.amount);
    }
    return {
        clearingCents,
        clearedCents,
        committedCents,
        // Floored at 0: an over-committed partner (a payout recorded larger than
        // the cleared pool — a data defect) must read as "nothing further owed",
        // never as a negative that some caller renders as money owed BACK.
        payableCents: Math.max(0, clearedCents - committedCents),
        forfeitedCents,
        lifetimeCents: clearingCents + clearedCents,
        unscheduledCount,
    };
}
//# sourceMappingURL=partnerBalance.js.map